<template>
  <cs-app
    id="inspire"
    :show-header="showHeader"
    :side-nav-width="sideNavWidth"
    :app-logo-uri="policyConnectLogo"
    :header-profile="headerProfile"
    :feedback-btn="feedbackBtn"
    @feedback-btn-click="showFeedbackView"
    :help-btn="helpBtn"
    :search-btn="searchBtn"
    @search-btn-click="showSearch"
    :customer-logo-uri="customerLogoUri"
    :customer-logo-width="250"
    :customer-name="customerName"
    :public-view="isPublicViewMode"
    :update-btn="updateBtn"
    @update-btn-click="checkUpdate"
    :view-btn="viewBtn"
    @view-btn-click="switchRole"
    :settings-btn="settingsBtn"
    @settings-btn-click="showSettingsView"
  >
    <template #right-side-nav>
      <side-panel-search v-if="isLoggedIn && !isFullScreen" @update-nav="NavTreeUpdate" />
    </template>

    <template v-slot:header-left>
      <div v-if="appBarBanner" id="app-banner" class="app-banner">
        <img :src="appBarBanner" alt="app banner" id="app-banner-img" />
      </div>
      <cs-button
        v-if="draftNavigationDisplayed && isAdminDraftView && isLoggedIn"
        id="app-header--create-btn"
        class="ml-4 toolbar-create-btn"
        label="Create"
        large
        primary
        @click.stop="openCreateDialog"
      ></cs-button>
    </template>

    <template v-slot:header-right>
      <div class="d-flex align-center justify-center">
        <div class="draft-switch">
          <v-tooltip v-if="isLoggedIn && draftNavigationDisplayed" location="bottom">
            <template v-slot:activator="{ props }">
              <div
                v-bind="props"
                class="mt-5 mr-2"
                :disabled="!draftNavigationDisplayed"
                @mousedown="setMouseDown"
                @mouseup="checkSwitchToggle"
                id="app-header--switch-draft-mode-switch"
              >
                <v-switch color="primary" v-model="draftMode" density="compact"> </v-switch>
              </div>
            </template>
            <span id="app-header--switch-draft-mode-tooltip">
              {{ draftMode ? 'Disable' : 'Enable' }} Draft Mode
            </span>
          </v-tooltip>
        </div>
        <div class="mazlan-icon">
          <v-tooltip v-if="mazlanDisplayed" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                id="app-header--mazlan-ai-btn"
                icon
                class="ma-0 mr-2"
                :class="
                  mazlanDialogDisplayed ? 'cs-header-icon-btn-active' : 'cs-header-icon-btn-default'
                "
                @click.stop="openMazlanDialog"
              >
                <div
                  class="ai-icon"
                  :style="{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'white',
                    mask: `url(${iAiIcon}) no-repeat center`,
                    maskSize: 'contain',
                    WebkitMask: `url(${iAiIcon}) no-repeat center`,
                    WebkitMaskSize: 'contain'
                  }"
                ></div>
              </v-btn>
            </template>
            <span class="cs-header-icon-btn-tooltip">Mazlan AI Assistant</span>
          </v-tooltip>
          <create-dialog
            v-if="isLoggedIn"
            :show-dialog="createDialogDisplayed"
            @closed="createDialogDisplayed = false"
          >
          </create-dialog>
        </div>
        <!-- Mazlan AI Assistant Container -->
        <div
          v-if="mazlanDisplayed"
          id="mazlan-ai-container"
          class="mazlan-ai-container"
          :data-dialog-open="mazlanDialogDisplayed"
        >
          <!-- ReactConnector will render DraggableContainer when dialog is open -->
        </div>
      </div>
    </template>

    <template v-slot:side-nav>
      <app-side-nav v-if="isLoggedIn && !isFullScreen" ref="nav"></app-side-nav>
      <app-side-nav-public
        v-if="!isLoggedIn && !isFullScreen && isPublicViewMode"
        ref="nav"
      ></app-side-nav-public>
    </template>

    <template v-slot:router-view>
      <refresh-token v-if="!isPublicViewMode"></refresh-token>
      <router-view v-slot="{ Component }">
        <v-slide-y-transition>
          <component :is="Component" @update-nav="NavTreeUpdate" />
        </v-slide-y-transition>
      </router-view>
    </template>
  </cs-app>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import {
  CSBase,
  CSHeaderButtonProps,
  CSHeaderActiveButtonProps,
  CSHeaderProfileProps
} from '@complispace/cs-design-system';
import * as types from '@/store/mutationTypes';
import AppSideNav from '@/components/core/AppSideNav';
import AppSideNavPublic from '@/components/core/AppSideNavPublic';
import PageHistory from '@/components/core/PageHistory';
import RefreshToken from '@/components/core/RefreshToken';
import SidePanelSearch from '@/components/content/SidePanelSearch';
import routerUtils from '@/lib/routerUtils';
import sessionManager from '@/store/modules/session/sessionManager';
import {
  COMPONENT_STATE,
  APP_STATE,
  COMPONENT_MANAGER_TYPE,
  VIEW_TYPES,
  LINKS,
  ROLES,
  APPCUES_TRACKABLE_EVENTS
} from '@/constants';
import policyConnectLogo from '@/resources/assets/ideagen-policy-logo.svg';
import policyConnectParentBanner from '@/resources/assets/banner/ideagen-policy-parent-banner.svg';
import policyConnectChildBanner from '@/resources/assets/banner/ideagen-policy-child-banner.svg';
import iAiIcon from '@/resources/assets/i-ai.svg';
import pkg from '../package.json';
import CreateDialog from './components/content/CreateDialog';
import { ReactConnector } from './components/mazlan/Connector.tsx';

export default {
  name: 'App',

  extends: CSBase,

  components: {
    AppSideNav,
    AppSideNavPublic,
    PageHistory,
    SidePanelSearch,
    RefreshToken,
    CreateDialog
  },

  data() {
    return {
      policyConnectLogo,
      iAiIcon,
      loading: false,
      items: [],
      search: null,
      showSearchNav: false,
      profileDisplayed: false,
      createDialogDisplayed: false,
      mazlanDialogDisplayed: false,
      comMode: false,
      draftMode: false,
      mouseDown: false,
      helpPortalLink: LINKS.POLICY_HELP_PORTAL,
      appBarLogo: policyConnectLogo,
      mazlanConnector: null // Track the Mazlan AI connector instance
    };
  },

  emits: ['setAppState'],

  computed: {
    ...mapState({
      app: (state) => state.app,
      cursor: (state) => state.appearance.cursor,
      isFullScreen: (state) => state.appearance.isFullScreen,
      isPublicViewMode: (state) => state.appearance.isPublicViewMode,
      isOnAhaPage: (state) => state.appearance.isOnAhaPage,
      profile: (state) => state.session.profile,
      appearance: (state) => state.appearance,
      client: (state) => state.client,
      role: (state) => state.authorisation.role,
      originalAdminRole: (state) => state.authorisation.originalAdminRole,
      clientAccessDetails: (state) => state.client.accessDetails,
      isPreferencesView: (state) => state.appearance.isPreferencesView,
      viewType: (state) => state.authorisation.viewType,
      isMobile: (state) => state.appearance.isMobile,
      isSearchPanelView: (state) => state.appearance.showSearchPanel
    }),

    ...mapGetters({
      contextItems: 'clientContext/items',
      contextValues: 'clientContext/values',
      getContextItem: 'clientContext/getContextItem',
      isLoggedIn: 'authorisation/isLoggedIn',
      isAllowedTo: 'authorisation/isAllowedTo',
      hasAccess: 'authorisation/hasAccess',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      authorizationUri: 'session/authorizationUri',
      authorizationUriComplispaceStaff: 'session/authorizationUriComplispaceStaff',
      appName: 'app/name',
      isLoginBannerEnabled: 'client/isLoginBannerEnabled',
      isMazlanEnabled: 'client/isMazlanEnabled',
      isFirstLogin: 'session/isFirstLogin',
      isLoginBannerShown: 'session/isLoginBannerShown',
      user: 'cs-session/user'
    }),

    feedbackBtn() {
      if (this.isLoggedIn) {
        const id = 'app-header--feedback';
        const toolTip = 'Feedback';
        return new CSHeaderButtonProps(id, toolTip);
      }
      return undefined;
    },

    helpBtn() {
      if (this.isLoggedIn) {
        const id = 'app-header--help-portal';
        const toolTip = 'Help';
        const href = this.helpPortalLink;
        return new CSHeaderButtonProps(id, toolTip, href);
      }
      return undefined;
    },

    searchBtn() {
      if (this.isLoggedIn) {
        const id = 'app-header--search';
        const toolTip = 'Search';
        const active = this.isSearchPanelView;
        return new CSHeaderActiveButtonProps(id, toolTip, undefined, undefined, active);
      }
      return undefined;
    },

    updateBtn() {
      if (this.isLoggedIn && (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2))) {
        const id = 'app-header--check-for-updates';
        const toolTip = 'Check For Updates';
        return new CSHeaderButtonProps(id, toolTip, undefined, undefined);
      }
      return undefined;
    },

    viewBtn() {
      if (this.isLoggedIn && this.isAdminRole) {
        const id = 'app-header--switch-view';
        const toolTip = this.switchRoleViewLabel;
        const active = this.viewType !== VIEW_TYPES.CLIENT;
        return new CSHeaderActiveButtonProps(id, toolTip, undefined, undefined, active);
      }
      return undefined;
    },

    settingsBtn() {
      if (this.isLoggedIn && this.hasAccess('contextviewaccessTable')) {
        const id = 'app-header--settings';
        const toolTip = 'Settings';
        return new CSHeaderButtonProps(id, toolTip, undefined, undefined);
      }
      return undefined;
    },

    customerLogoUri() {
      if (this.isPreferencesView) {
        return null;
      }

      return this.client.logoUri || '';
    },

    customerName() {
      if (this.isPreferencesView) {
        return null;
      }

      return this.client.displayName || 'Your PolicyConnect Site';
    },

    version() {
      const { bundleVersion } = pkg;
      const uiVersion = pkg.version;
      const dashApiVersion = this.app.apiVersion ? `-${this.app.apiVersion}` : '';
      const contentEngineVersion = this.app.ceVersion ? `-${this.app.ceVersion}` : '';
      return `${bundleVersion}#${uiVersion}${dashApiVersion}${contentEngineVersion}`;
    },

    headerProfile() {
      if (!this.$store.getters['authorisation/auth0Enabled']) {
        const user = {
          givenName: this.profile.name.givenName,
          surname: this.profile.name.familyName,
          email: this.profile.email
        };
        const actions = {
          logout: {
            action: this.logout
          }
        };
        return new CSHeaderProfileProps(user, actions);
      }
      // This is for auth0;
      return new CSHeaderProfileProps(
        {
          givenName: this.user?.givenName || this.user?.name || '',
          surname: this.user?.surname || '',
          email: this.user?.email || ''
        },
        {
          logout: {
            action: this.logout
          }
        }
      );
    },

    sideNavWidth() {
      if (this.isOnAhaPage) {
        return 0;
      }

      if (this.isLoggedIn) {
        return 400;
      }

      if (this.isPublicViewMode) {
        return 350;
      }

      return 0;
    },

    draftNavigationDisplayed() {
      const { isUgcEnabled } = this.clientAccessDetails;
      const result =
        isUgcEnabled &&
        !this.isPreferencesView &&
        (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2));

      return result;
    },

    mazlanDisplayed() {
      return this.isLoggedIn && this.isAdminRole && this.isMazlanEnabled;
    },

    switchRoleViewLabel() {
      return this.viewType === VIEW_TYPES.CLIENT ? 'Administrator View' : 'Client User View';
    },

    shouldDisplayLoginBanner() {
      return this.isLoginBannerEnabled && this.isFirstLogin && !this.isLoginBannerShown;
    },

    isAdminRole() {
      if (
        this.role > ROLES.CLIENT_USER ||
        (this.role === ROLES.CLIENT_USER && this.originalAdminRole > ROLES.CLIENT_USER)
      ) {
        return true;
      }

      if (
        this.isAllowedTo(ROLES.CS_ADMIN) ||
        this.isAllowedTo(ROLES.GROUP_ADMIN) ||
        this.isAllowedTo(ROLES.CLIENT_ADMIN)
      ) {
        return true;
      }

      return false;
    },

    isGroupAdminRole() {
      if (
        this.role >= ROLES.GROUP_ADMIN ||
        (this.role === ROLES.CLIENT_USER && this.originalAdminRole >= ROLES.GROUP_ADMIN)
      ) {
        return true;
      }
      if (this.isAllowedTo(ROLES.CS_ADMIN) || this.isAllowedTo(ROLES.GROUP_ADMIN)) {
        return true;
      }
      return false;
    },

    isGroupParent() {
      if (this.client.children && this.client.children.length > 0) {
        return true;
      }
      return false;
    },

    isGroupChild() {
      const { parentId } = this.client;
      const isGroupChild = parentId !== null && parentId !== undefined;
      return isGroupChild;
    },

    appBarBanner() {
      if (this.isGroupAdminRole) {
        if (this.isGroupChild) return policyConnectChildBanner;
        if (this.isGroupParent) return policyConnectParentBanner;
      }
      return null;
    },

    showHeader() {
      return !this.isOnAhaPage && (this.isLoggedIn || this.isPublicViewMode);
    }
  },

  async mounted() {
    // Start up procedure
    const { plugIns } = this.$app;
    plugIns.initialPlugIn();

    try {
      let applicationName = sessionManager.getAppNameFromLocalStorage();
      if (!applicationName) {
        applicationName = await this.$store.dispatch('getAppNameFromHost');
      }
      document.title = applicationName;
      await this.$store.dispatch('appInitialise', pkg.version, { root: true });
      await this.$store.dispatch('initAppName', applicationName, { root: true });
      if (document.title !== this.appName) document.title = this.appName;
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        this.showErrorResponseAlert(err);
      }

      if (err.message === 'Network Error') {
        this.$router.push('servernotavailable').catch(routerUtils.handleNavigationFailures);
      } else {
        this.$router.push('somethingwentwrong').catch(routerUtils.handleNavigationFailures);
      }
      console.error(err);
    }

    if (
      navigator.userAgent.match(
        /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
      )
    ) {
      this.$store.commit(types.APPEARANCE_SET_ISMOBILE, true);
    }

    this.$nextTick(() => {
      setTimeout(() => {
        if (this.isPublicViewMode) {
          this.setShowSideNav(false);
        }
      }, 5000);
    });

    this.draftMode = this.isAdminDraftView;

    // Initialize Mazlan AI connector when app mounts
    this.$nextTick(() => {
      this.initializeMazlanConnector();
    });
  },

  beforeUnmount() {
    // Clean up Mazlan connector when component is destroyed
    this.cleanupMazlanConnector();
  },

  watch: {
    contextValues() {
      const CSPColours = this.getContextItem('CSPColours');
      if (CSPColours) {
        const contextTheme = CSPColours.value;
        try {
          const ctp = JSON.parse(contextTheme);
          if (ctp && ctp.primary) this.$vuetify.theme = ctp;
        } catch (e) {
          console.log('Unable to process CSPColours : ', e);
        }
      }
    },
    cursor(value) {
      switch (value) {
        case 'progress':
          document.body.classList.add('busy-cursor');
          break;
        default:
          document.body.classList.remove('busy-cursor');
          break;
      }
    },
    shouldDisplayLoginBanner(newValue) {
      if (newValue) {
        const message =
          "Have you found PolicyConnect to be running a bit slow lately? <func>Click here</func> to see what we're doing about it";
        const policyConnectPerformanceUrl =
          'https://complispace.zendesk.com/hc/en-us/articles/9629627288591-PolicyConnect-Performance';
        const options = {
          func: () => this.openUrlInNewTab(policyConnectPerformanceUrl)
        };
        this.showInfoAlert(message, false, options);
        this.setIsLoginBannerShown(true);
        setTimeout(() => {
          this.clearAlert(); // close info alert after 15 seconds
        }, 15000);
      }
    },

    mazlanDisplayed(newValue) {
      if (newValue) {
        // Mazlan should be displayed, initialize it
        this.$nextTick(() => {
          this.initializeMazlanConnector();
        });
      } else {
        // Mazlan should be hidden, clean it up
        this.cleanupMazlanConnector();
      }
    },

    mazlanDialogDisplayed(newValue) {
      if (this.mazlanConnector) {
        // Update existing connector with new state
        this.updateMazlanConnector();
      } else if (newValue) {
        // Dialog opened - initialize the React component
        this.$nextTick(() => {
          this.initializeMazlanConnector();
        });
      }
    }
  },

  methods: {
    async logout() {
      await this.$store.dispatch('authorisation/logout');
      await this.$router.go('/');
    },

    checkUpdate() {
      this.$refs.nav.getUpdate(true);
    },

    NavTreeUpdate(details) {
      this.$refs.nav.treeUpdate(details);
    },

    getAppState() {
      let res = APP_STATE.STATE_IDLE;
      if (this.$refs && this.$refs.nav && !this.isPublicViewMode) {
        const comState = this.$refs.nav.getComponentState();
        if (comState === COMPONENT_STATE.STATE_EDITING) {
          res = APP_STATE.STATE_EDITING;
        }
      }
      return res;
    },

    setAppState(state) {
      if (this.$refs && this.$refs.nav) {
        if (state === APP_STATE.STATE_IDLE) {
          this.$refs.nav.updateComponent(COMPONENT_MANAGER_TYPE.TYPE_COM_STATE, {
            state: COMPONENT_STATE.STATE_IDLE
          });
        }
      }
    },

    ...mapMutations({
      setSearchPanel: types.APPEARANCE_SET_SEARCH_PANEL,
      setIsFirstLogin: types.SESSION_SET_IS_FIRST_LOGIN,
      setIsLoginBannerShown: types.SESSION_SET_IS_LOGIN_BANNER_SHOWN
    }),

    showSearch() {
      this.setSearchPanel(true);
    },

    showSettingsView() {
      if (this.editingCheck()) {
        this.$emit('setAppState', APP_STATE.STATE_IDLE);
        this.$router.push('/preferences/context').catch(routerUtils.handleNavigationFailures);
      }
    },

    initializeMazlanConnector() {
      // Check if mazlan should be displayed
      if (!this.mazlanDisplayed) {
        return;
      }

      try {
        // Clean up any existing instance first
        if (this.mazlanConnector) {
          this.mazlanConnector.unmount();
          this.mazlanConnector = null;
        }

        const mazlanContainer = document.getElementById('mazlan-ai-container');
        if (!mazlanContainer) {
          setTimeout(() => this.initializeMazlanConnector(), 100);
          return;
        }

        // Create React app - it manages itself from now on
        this.mazlanConnector = new ReactConnector(mazlanContainer);
        // Initial render with current state
        this.updateMazlanConnector();
      } catch (error) {
        console.error('Failed to initialize Mazlan AI Connector:', error);
      }
    },

    updateMazlanConnector() {
      if (this.mazlanConnector) {
        // Pass token from Vue as default value
        this.$store
          .dispatch('cs-session/getToken')
          .then((vueToken) => {
            this.mazlanConnector.render({
              open: this.mazlanDialogDisplayed,
              token: vueToken, // Pass Vue token as default
              onClose: () => {
                this.mazlanDialogDisplayed = false;
              }
            });
          })
          .catch((error) => {
            console.error('Failed to update Mazlan AI Connector:', error);
          });
      }
    },

    cleanupMazlanConnector() {
      if (this.mazlanConnector) {
        try {
          this.mazlanConnector.unmount();
          this.mazlanConnector = null;
        } catch (error) {
          console.error('Failed to cleanup Mazlan AI Connector:', error);
        }
      }
    },

    showContextView() {
      this.$router.push('/preferences/context').catch(routerUtils.handleNavigationFailures);
    },

    showGroupAdminView() {
      this.$router.push('/preferences/groupAdmin').catch(routerUtils.handleNavigationFailures);
    },

    showFeedbackView() {
      if (this.editingCheck()) {
        this.$emit('setAppState', APP_STATE.STATE_IDLE);
        this.$router.push('/feedback').catch(routerUtils.handleNavigationFailures);
      }
    },

    switchRole() {
      try {
        if (this.role === ROLES.CLIENT_USER) {
          const { plugIns } = this.$app;

          this.$store.dispatch('authorisation/saveUserRole', this.originalAdminRole);
          plugIns.appcuesTrackEvent(APPCUES_TRACKABLE_EVENTS.SWITCHED_TO_ADMIN_VIEW);
        } else if (this.editingCheck()) {
          this.$store.dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER);
        }
      } catch (err) {
        console.error(err);
      }
    },

    goToHome() {
      if (this.$router.currentRoute.name === 'Dashboard') {
        // give loading impression although nothing is really to update when clicking
        // Home on Home (Dashboard) page
        this.setLoading(true);

        this.$router.push('/').catch(routerUtils.handleNavigationFailures);

        setTimeout(() => {
          this.setLoading(false);
        }, 300);

        return;
      }

      this.$router.push('/').catch(routerUtils.handleNavigationFailures);
    },

    goBack() {
      this.$store.commit(types.APPEARANCE_SET_FULLSCREEN, false);
      this.$router.back();
    },

    profileToggle() {
      this.profileDisplayed = !this.profileDisplayed;
    },

    openCreateDialog() {
      this.createDialogDisplayed = true;
    },

    openMazlanDialog() {
      this.mazlanDialogDisplayed = true;
    },

    editingCheck() {
      let isContinue = true;

      // TODO: Unify Interface over adminRole judgement
      // the current situation is use isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2) which is redundant but used everywhere
      // isAdminRole seems better but with a vague property originalAdminRole
      if (
        this.appState === APP_STATE.STATE_EDITING &&
        (this.isAllowedTo(ROLES.CS_ADMIN) ||
          this.isAllowedTo(ROLES.GROUP_ADMIN) ||
          this.isAllowedTo(ROLES.CLIENT_ADMIN))
      ) {
        isContinue = window.confirm(
          'Do you really want to leave?\nChanges that you made may not be saved.'
        );
      }
      return isContinue;
    },

    setMouseDown() {
      this.mouseDown = true;
    },

    // there is no way to set the v-module property for v-swtich
    // use mouseup event to set the property to prevent toggling.
    // It is the only option for now. If there is beforeChange prop in the future, change it.
    checkSwitchToggle() {
      if (this.mouseDown) {
        if (this.draftMode) {
          this.$store.dispatch('authorisation/saveDraftMode', false);
        } else if (this.editingCheck()) {
          this.$store.dispatch('authorisation/saveDraftMode', true);
        } else {
          this.draftMode = !this.draftMode;
        }
      }
      this.mouseDown = false;
    },
    openUrlInNewTab(url) {
      window.open(url, '_blank');
    }
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
}

html,
body {
  height: 100%;
}

/*
    body {
        cursor: url('./resources/assets/cursor.png'), auto;
    }
    */

body.busy-cursor {
  cursor: progress;
}

.vertical-center {
  display: flex;
  align-items: center;
}

.bottom-container {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
}

a {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.center-it {
  background-position: center;
  justify-content: center;
  text-align: center;
  justify-content: center;
  align-items: center;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-split-bar {
  background-color: transparent;
  width: 0px;
  z-index: 100;
  height: 100vh;
  top: 58px;
  margin-left: 360px;
  position: fixed;
}

.app-split-bar-collapsed {
  z-index: 3;
  height: 100vh;
  top: 58px;
  width: 20px;
  margin-left: 0px;
  position: fixed;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.app-split-bar .app-split-bar-arrow {
  position: relative;
  top: 12px;
  left: 22px;
  z-index: 1;
}

.app-split-bar-collapsed .app-split-bar-arrow {
  position: relative;
  top: 12px;
  left: 2px;
  z-index: 1;
}

#main-container {
  background-color: #fff;
}

#cs-side-nav {
  background-color: #eff3f6;
}

.footer {
  background-color: white !important;
}

.header-right .draft-switch {
  width: 46px;
}

.header-right .draft-switch .v-input__control {
  display: flex;
  justify-content: flex-end;
}

.cs-header-app-bar-content {
  padding-left: 40px !important;
}

.app-banner {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
}

.mazlan-ai-container {
  position: fixed !important;
  bottom: 24px;
  right: 24px;
  z-index: 2000;
}

.MuiPaper-root {
  z-index: 2010;
}

p.MuiTypography-root {
  margin-bottom: 0 !important;
}

.MuiDialogTitle-root {
  z-index: 2020;
}

.MuiDialogTitle-root button {
  z-index: 2025;
}
</style>
<style scoped>
.v-tooltip__content--fixed {
  position: fixed;
}

.v-tooltip__content {
  z-index: 1500 !important;
  top: 72px !important;
}

.menuable__content__active {
  z-index: 1500 !important;
  top: 72px !important;
}

:deep(div.v-switch__track) {
  background-color: rgba(0, 0, 0, 0.38);
  opacity: 0.6;
}
</style>

<style scoped>
:deep(.hx-content-card) {
  padding: 0;
  & .content-card-body {
    padding: 16px;
  }
}
</style>
